import {
  CLOSE_ACCESS_CHANGED,
  CLOSE_SESSION_INVALID,
  CLOSE_STREAM_TAKEN,
  PROTOCOL_VERSION,
  type ServerMessage,
} from "@screener/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCurrentUser } from "@/lib/auth-api";
import { holdKeepAwakeLock } from "./keep-awake";

/**
 * `taken_over` — поток забрала другая вкладка (§131): переподключения нет, пока
 * человек сам не нажмёт «Открыть здесь».
 */
export type ConnectionStatus = "connecting" | "open" | "disconnected" | "taken_over";

/** Что делать с закрытием сокета, судя по коду (коды — из `@screener/protocol`). */
export type CloseOutcome = "session_invalid" | "taken_over" | "access_changed" | "reconnect";

/**
 * Решение по коду закрытия: `4401` — отказ по сессии (форма входа), `4409` —
 * поток у другой вкладки (экран «открыт в другой вкладке», без таймера),
 * `4403` — доступ по тарифу изменился (переподключение сразу, `me` перечитывается),
 * прочее — обычный разрыв с переподключением.
 */
export function classifyClose(code: number): CloseOutcome {
  if (code === CLOSE_SESSION_INVALID) {
    return "session_invalid";
  }
  if (code === CLOSE_STREAM_TAKEN) {
    return "taken_over";
  }
  if (code === CLOSE_ACCESS_CHANGED) {
    return "access_changed";
  }
  return "reconnect";
}

/**
 * Статус после снятия эффекта с сокетом (выход, потеря сессии): `taken_over`
 * не должен пережить его, иначе после повторного входа мелькнул бы экран
 * «открыт в другой вкладке» до первого подключения.
 */
export function statusAfterTeardown(status: ConnectionStatus): ConnectionStatus {
  return status === "taken_over" ? "connecting" : status;
}

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 15000;

/**
 * Пауза перед переподключением: после смены доступа её нет (демон закрыл поток
 * намеренно, чтобы приветствие пришло уже с новым составом), иначе — ступень
 * лестницы `attempt`.
 */
export function reconnectDelayMs(outcome: CloseOutcome, attempt: number): number {
  if (outcome === "access_changed") {
    return 0;
  }
  return Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
}

/** Сколько раз подряд поток переоткрывается сразу после `4403` в окне ниже. */
export const ACCESS_RECONNECT_LIMIT = 2;
export const ACCESS_RECONNECT_WINDOW_MS = 30_000;

/**
 * Можно ли переподключиться сразу после очередного `4403`. Демон, который
 * закрывает поток кодом «доступ изменился» каждый раз, не должен получать
 * переподключения без паузы бесконечно: больше `ACCESS_RECONNECT_LIMIT` раз за
 * `ACCESS_RECONNECT_WINDOW_MS` третий идёт по обычной лестнице пауз. Вход —
 * моменты прошлых немедленных переподключений; выход — их же с учётом нового.
 */
export function accessReconnect(
  recentMs: readonly number[],
  nowMs: number,
): { immediate: boolean; recentMs: number[] } {
  const inWindow = recentMs.filter((at) => nowMs - at < ACCESS_RECONNECT_WINDOW_MS);
  if (inWindow.length >= ACCESS_RECONNECT_LIMIT) {
    return { immediate: false, recentMs: inWindow };
  }
  return { immediate: true, recentMs: [...inWindow, nowMs] };
}

/**
 * Сколько поток должен продержаться открытым, прежде чем лестница пауз
 * переподключения сбрасывается. Сброс по самому факту `open` заставлял бы
 * клиента слать запрос раз в секунду бесконечно демону, который принимает
 * апгрейд и тут же рвёт соединение (падение в цикле, прокси) — `open` там
 * происходит каждый раз, а полезного соединения не случается (design
 * review stage5-auth-sessions, пункт 4).
 */
const STABLE_CONNECTION_MS = 5000;

interface UseIpcConnectionOptions {
  /** Поток держим, только пока в клиенте есть действительная сессия. */
  enabled: boolean;
  /** Поток закрылся кодом отказа по сессии — не обычный разрыв связи. */
  onSessionInvalid: () => void;
  /** Поток закрыт кодом «доступ изменился»: тариф перечитывается, поток открывается заново. */
  onAccessChanged?: () => void;
  /**
   * Пришло сообщение потока. Хук остаётся жизненным циклом сокета и о составе
   * сообщения ничего не знает: разбор и накопление живут выше.
   */
  onMessage?: (message: ServerMessage) => void;
}

/** Адрес потока: версия протокола в запросе, чтобы демон выбрал вид сообщений. */
export function ipcUrl(): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ipc?protocol=${PROTOCOL_VERSION}`;
}

/**
 * Жизненный цикл websocket-потока `/ipc`. Обычный разрыв связи ведёт к
 * переподключению с нарастающей паузой; закрытие кодом `4401` — к выходу на
 * форму входа без дальнейших попыток (спека `web-login`, сценарий «Сессия
 * истекла при открытом экране»).
 *
 * Демон отвергает апгрейд ответом HTTP `401` до открытия сокета — браузер
 * отдаёт это как `close` с кодом `1006`, неотличимым от «демон не отвечает».
 * Сокет, закрывшийся, так и не открывшись, поэтому дополнительно сверяется
 * с `GET /api/auth/me`: `401` там — тот же отказ по сессии, что и код
 * `4401`; любой другой исход — обычный разрыв, лестница пауз продолжается
 * (design review stage5-auth-sessions, пункт 1).
 */
export function useIpcConnection({
  enabled,
  onSessionInvalid,
  onAccessChanged,
  onMessage,
}: UseIpcConnectionOptions) {
  // Состояние и момент его наступления — одно значение: §9 требует показать
  // не только «данные устарели», но и с какого момента, а два независимых
  // сеттера дали бы промежуточный кадр с чужой парой.
  const [connection, setConnection] = useState<{
    status: ConnectionStatus;
    sinceMs: number;
  }>(() => ({ status: "connecting", sinceMs: Date.now() }));
  // Счётчик ручных открытий: его смена пересоздаёт эффект с сокетом.
  const [attemptId, setAttemptId] = useState(0);
  const onSessionInvalidRef = useRef(onSessionInvalid);
  const onAccessChangedRef = useRef(onAccessChanged);
  const onMessageRef = useRef(onMessage);
  const statusRef = useRef<ConnectionStatus>("connecting");

  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);

  useEffect(() => {
    onAccessChangedRef.current = onAccessChanged;
  }, [onAccessChanged]);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const moveTo = useCallback((next: ConnectionStatus) => {
    statusRef.current = next;
    setConnection((previous) =>
      previous.status === next ? previous : { status: next, sinceMs: Date.now() },
    );
  }, []);

  // `attemptId` в зависимостях намеренно: его смена («Открыть здесь») заново
  // запускает эффект, то есть открывает соединение тем же путём, что первое.
  // biome-ignore lint/correctness/useExhaustiveDependencies: триггер повторного открытия
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Одна блокировка на время жизни эффекта, а не на каждый сокет.
    const releaseKeepAwake = holdKeepAwakeLock();
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let stableTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let accessReconnects: number[] = [];
    let socket: WebSocket | undefined;

    const scheduleReconnect = (outcome: CloseOutcome = "reconnect") => {
      const delay = reconnectDelayMs(outcome, attempt);
      if (outcome !== "access_changed") {
        attempt += 1;
      }
      reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      moveTo("connecting");
      let didOpen = false;
      const nextSocket = new WebSocket(ipcUrl());
      socket = nextSocket;

      nextSocket.addEventListener("open", () => {
        if (cancelled) {
          return;
        }
        didOpen = true;
        moveTo("open");
        stableTimer = setTimeout(() => {
          attempt = 0;
        }, STABLE_CONNECTION_MS);
      });

      nextSocket.addEventListener("message", (event) => {
        if (cancelled || typeof event.data !== "string") {
          return;
        }
        // Разбор состава — не дело жизненного цикла сокета: сюда попадает
        // только то, что вообще не является сообщением потока.
        let message: ServerMessage;
        try {
          message = JSON.parse(event.data) as ServerMessage;
        } catch {
          return;
        }
        onMessageRef.current?.(message);
      });

      nextSocket.addEventListener("close", (event) => {
        if (socket === nextSocket) {
          socket = undefined;
        }
        if (stableTimer !== undefined) {
          clearTimeout(stableTimer);
          stableTimer = undefined;
        }
        if (cancelled) {
          return;
        }
        const outcome = classifyClose(event.code);
        if (outcome === "session_invalid") {
          onSessionInvalidRef.current();
          return;
        }
        if (outcome === "taken_over") {
          // Таймер не заводится: вкладка, у которой отняли поток, не должна
          // отнимать его обратно сама (§131 п. 3).
          // Вкладка без потока не держится незамороженной: блокировка
          // отпущена, «Открыть здесь» перезапустит эффект и возьмёт её заново.
          releaseKeepAwake();
          moveTo("taken_over");
          return;
        }
        if (outcome === "access_changed" && didOpen) {
          // Демон закрыл поток сам, потому что состав доступа сменился: ждать
          // нечего. Приветствие нового потока придёт уже с новым составом, а
          // подпись тарифа и замки перечитываются из `me`.
          onAccessChangedRef.current?.();
          const next = accessReconnect(accessReconnects, Date.now());
          accessReconnects = next.recentMs;
          if (next.immediate) {
            scheduleReconnect("access_changed");
            return;
          }
          // Третий `4403` в окне — обычная лестница пауз, а не цикл без пауз.
          moveTo("disconnected");
          scheduleReconnect();
          return;
        }
        moveTo("disconnected");
        if (didOpen) {
          scheduleReconnect();
          return;
        }
        // Никогда не открылся: рукопожатие могло отвергнуть сессию (HTTP
        // `401`) или демон в самом деле недоступен — на уровне сокета это
        // одно и то же событие, различает только отдельный запрос.
        fetchCurrentUser().then((result) => {
          if (cancelled) {
            return;
          }
          if (result.status === "anonymous") {
            onSessionInvalidRef.current();
            return;
          }
          scheduleReconnect();
        });
      });
    };

    connect();

    return () => {
      cancelled = true;
      releaseKeepAwake();
      moveTo(statusAfterTeardown(statusRef.current));
      if (reconnectTimer !== undefined) {
        clearTimeout(reconnectTimer);
      }
      if (stableTimer !== undefined) {
        clearTimeout(stableTimer);
      }
      socket?.close();
      socket = undefined;
    };
  }, [enabled, moveTo, attemptId]);

  // «Открыть здесь»: новое открытие тем же путём, что первое подключение, —
  // эффект пересоздаётся и вызывает `connect()` с нулевой лестницей пауз.
  const openHere = useCallback(() => {
    setAttemptId((previous) => previous + 1);
  }, []);

  return { status: connection.status, statusSinceMs: connection.sinceMs, openHere };
}
